import React, { useState, useEffect, useRef, KeyboardEvent } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdownMenu";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icons";
import useHandleSearchParams from "@/hooks/useHandleSearchParams";
import { useRefinementList } from "react-instantsearch";
import {
  FACET_FETCH_LIMIT,
  FILTER_CATEGORIES_NAMES_MAP,
  MIN_ITEMS_TO_SHOW_SEARCH_FILTER,
  LEVEL_PRIORITY,
  AVAILABILITY_PRIORITY,
} from "@/components/Search/constants";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import { FieldSelected } from "@/hooks/eventing/types/redventures.usertracking.v3.FieldSelected";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import {
  getCorrelationID,
  getPageType,
  getWebContext,
} from "@/utils/tagular/helpers";
import { useInView } from "react-intersection-observer";
import getSortedRefinementItems from "@/components/Search/utils/getSortedRefinementItems";
import { usePathname } from "next/navigation";
import { useSearchIndex } from "@/components/Search/components/SearchContextProvider";
import { cn } from "@/lib/utils";
import { RefinementItem } from "@/components/Search/types";
import { filterCertificateWithoutSubValue } from "@/components/Search/utils/filterCertificateWithoutSubValue";
import DropdownCheckboxItem from "./DropdownCheckbox";

const FilterDropdown = ({
  filterCategory,
  contentClassName = "",
  labelFontSize = "text-md",
}: {
  filterCategory: string;
  contentClassName?: string;
  labelFontSize?: string;
}) => {
  const { searchIndex } = useSearchIndex();
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const { setSearchParam, removeSearchParam } = useHandleSearchParams();
  const { items, refine } = useRefinementList({
    attribute: filterCategory,
    sortBy: ["isRefined", "count:desc"],
    limit: FACET_FETCH_LIMIT,
    showMore: false,
    escapeFacetValues: true,
  });
  const numberOfRefinedItems = items.filter((item) => item.isRefined).length;
  const { fieldSelected } = useFormEvents({});
  const pathname = usePathname();

  // Determine position based on filter category index in FILTER_CATEGORIES_NAMES_MAP
  const position = Object.keys(FILTER_CATEGORIES_NAMES_MAP)
    .indexOf(filterCategory)
    .toString();

  // Get filter label
  const filterLabel =
    FILTER_CATEGORIES_NAMES_MAP[filterCategory] || filterCategory;

  // Generate htmlId
  const htmlId = `filter-${filterCategory}-trigger`;

  // Element events for filter trigger button
  const { elementClicked: filterTriggerClicked, elementViewed } =
    useElementEvents({
      webElement: {
        elementType: ElementType.Button,
        htmlId,
        location: "degree-hub-filters",
        position,
        text: filterLabel,
      },
    });

  const { ref: dropdownRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const hasTriggeredViewEvent = useRef(false);

  useEffect(() => {
    if (inView && !hasTriggeredViewEvent.current) {
      elementViewed();
      hasTriggeredViewEvent.current = true;
    }
  }, [inView, elementViewed]);

  // Handle filter trigger button click event
  const handleFilterTriggerClick = () => {
    if (typeof window === "undefined") return;

    // Extract page subcategory from pathname (e.g., "/bachelors" -> "bachelors")
    const subcategory = pathname?.replace("/", "") || "";

    const base = getWebContext();
    const eventData = {
      webContext: {
        ...base,
        userAgent: base?.userAgent ?? undefined,
        page: {
          url: pathname || "",
        },
      },
      customDimensions: [
        { key: "group", value: "degree-hub" },
        { key: "subcategory", value: subcategory },
      ],
    };

    // Add page context as override
    filterTriggerClicked(eventData);
  };

  const handleRefinementClick = (
    payload: React.ChangeEvent<HTMLInputElement> | RefinementItem,
  ) => {
    const isEvent = "target" in payload;
    const value = isEvent ? payload.target.value : payload.value;
    const isChecked = isEvent ? payload.target.checked : payload.isRefined;

    if (typeof window !== "undefined") {
      const webBase = getWebContext();
      const formEvent: FieldSelected = {
        correlationId: getCorrelationID(),
        userInputField: {
          fieldName: value,
          fieldLabel: FILTER_CATEGORIES_NAMES_MAP[filterCategory],
          fieldType: "search-filters",
          fieldValue: isChecked ? "Selected" : "Deselected",
        },
        webContext: {
          ...webBase,
          userAgent: webBase?.userAgent ?? undefined,
          page: {
            title: document.title,
            url: window.location.href,
            pageType: getPageType(),
          },
        },
        "@type": "redventures.usertracking.v3.FieldSelected",
      };

      fieldSelected(formEvent);
    }
    if (isChecked) {
      setSearchParam(filterCategory, value);
    } else {
      removeSearchParam(filterCategory, value);
    }
    refine(value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const getFixedOrder = () => {
    if (filterCategory === "level") return LEVEL_PRIORITY;
    if (filterCategory === "availability") return AVAILABILITY_PRIORITY;
    return null;
  };

  const fixedOrder = getFixedOrder();

  const sortedItems = fixedOrder
    ? [...items].sort((a, b) => {
        // Refined items first
        const isRefinedDiff = Number(b.isRefined) - Number(a.isRefined);
        if (isRefinedDiff !== 0) return isRefinedDiff;
        // Then by fixed order
        const aIndex = fixedOrder.indexOf(a.label);
        const bIndex = fixedOrder.indexOf(b.label);
        return (
          (aIndex === -1 ? Infinity : aIndex) -
          (bIndex === -1 ? Infinity : bIndex)
        );
      })
    : getSortedRefinementItems(items, "count");

  const facetItems = filterCertificateWithoutSubValue(
    sortedItems,
    filterCategory,
  ).filter((item: { label: string }) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <DropdownMenu
      modal={false}
      open={open}
      onOpenChange={(next) => setOpen(next)}
    >
      <div ref={dropdownRef}>
        <DropdownMenuTrigger asChild>
          <Button
            id={`filter-${filterCategory}-trigger`}
            variant="primaryOutline"
            className={`font-normal border-2 whitespace-nowrap py-2.5 px-6 h-11 flex items-center gap-1 ${numberOfRefinedItems > 0 ? "border-[3px] font-semibold" : ""}`}
            onClick={handleFilterTriggerClick}
            aria-haspopup="menu"
          >
            <span className={`p-0 m-0 ${labelFontSize} hover:text-white`}>
              {`${FILTER_CATEGORIES_NAMES_MAP[filterCategory] || filterCategory}  ${numberOfRefinedItems > 0 ? `(${numberOfRefinedItems})` : ""}`}
            </span>
            <Icon
              type="keyboard-arrow-down-material"
              svgProps={{ "aria-hidden": true }}
            />
          </Button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent
        align="start"
        className={cn(
          "mx-2 w-[280px] max-h-[400px] overflow-y-auto scrollbar-thin gap-3 p-0 z-50",
          contentClassName,
        )}
      >
        {items.length > MIN_ITEMS_TO_SHOW_SEARCH_FILTER && (
          <div className="w-full relative">
            <input
              type="search"
              className="my-2 md:my-0 flex bg-white min-h-12 border-0 border-b border-gray-300 w-full text-sm"
              placeholder={`Search ${FILTER_CATEGORIES_NAMES_MAP[filterCategory].toLowerCase()} (${items.length})`}
              aria-label={`Search ${FILTER_CATEGORIES_NAMES_MAP[filterCategory].toLowerCase()}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                e.stopPropagation()
              }
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              {searchQuery ? (
                <button
                  onClick={handleClearSearch}
                  className="hover:text-gray-600"
                  aria-label="Clear search"
                  type="button"
                >
                  <Icon type="close" svgProps={{ "aria-hidden": true }} />
                </button>
              ) : (
                <Icon
                  type="manifying-glass"
                  svgProps={{ "aria-hidden": true }}
                />
              )}
            </div>
          </div>
        )}
        <div>
          {facetItems.map((facet, index) => (
            <DropdownMenuItem
              onSelect={(e) => {
                // this prevents the dropdown menu from closing when selecting an item
                e.preventDefault();

                handleRefinementClick({
                  ...facet,
                  isRefined: !facet.isRefined,
                });
              }}
              key={facet.value}
              className={cn(
                "hover:bg-putty-200 hover:cursor-pointer px-4",
                facet.isRefined && "bg-putty-200",
              )}
            >
              <DropdownCheckboxItem
                item={facet}
                handleRefinementClick={handleRefinementClick}
                filterCategory={filterCategory}
                position={index}
                indexName={searchIndex}
              />
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterDropdown;
